import { useMutation, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { useCallback, useContext, useMemo, useState } from "react";
import {
  Alert,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Form,
  Modal,
  Row,
} from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import type { AccountWORiskAllocation } from "../../../../api/src/accounts/accounts.service";
import type { BillingController } from "../../../../api/src/billing/billing.controller";
import type { AccountCashSetting } from "../../../../api/src/cash-settings/lib";
import type {
  EditableHousehold,
  FeeChargeLocation,
} from "../../../../api/src/households-base/households-base.service";
import type { HouseholdGroupForHousehold } from "../../../../api/src/household-groups/household-groups-on-date";
import type { HouseholdsController } from "../../../../api/src/households/households.controller";
import type { HouseholdWithAccounts } from "../../../../api/src/households/households.service";
import type { UnpackResponse } from "../../../../api/src/lib";
import {
  useQueryBillingMinimums,
  useQueryFeeStructures,
} from "../../billing/lib";
import {
  useQueryAccountCashSettings,
  useQueryCashSettings,
} from "../../cash/lib";
import type { ProfileController } from "../../../../api/src/profile/profile.controller";
import type { SlantBillingWriteBackController } from "../../../../api/src/slant/billing-write-back.controller";
import type { CashSetting } from "../../../../api/src/cash-settings/lib";
import Loading from "../../Loading";
import { NotificationContext } from "../../Notifications";
import ActionButton from "../../components/ActionButton";
import { useErrorMessage } from "../../components/FormError";
import FormFieldError from "../../components/FormFieldError";
import SubmitButton from "../../components/SubmitButton";
import { Table } from "../../components/Table/Table";
import {
  ApiError,
  extractServerMessage,
  serverError,
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../../lib/api";
import { InlineError, displayAccountName } from "../../lib/display";
import { getSchemaFieldLabel } from "../../lib/forms";
import { formatCurrency, formatPercent } from "../../lib/numbers";
import {
  HouseholdCashBillingRow,
  useHouseholdCashBillingTable,
} from "./HouseholdCashBillingTable";
import { HouseholdContext } from "./HouseholdInfo";

const feeChargeLocations: FeeChargeLocation[] = [
  "Custodian Billed",
  "Direct Billed",
];

/** Where an administrator chooses which figures go to Slant, and provisions them. */
const SLANT_BILLING_FIELDS_PAGE = "the firm's Billing Fields page";

/**
 * The Slant field keys a failed push says have no Slant definition, or `null`
 * when the failure is anything else.
 *
 * `POST /slant/billing-fields/households/:id/push` answers an unprovisioned
 * configured field with a 409 carrying `missingKeys`
 * (`api/src/slant/billing-write-back.controller.ts`), but `publicFetch`
 * (`lib/api.ts`) throws `Error("Request failed: <body>")` on any non-ok
 * response, so the structured body only survives inside that message. Mirrors
 * `extractServerMessage` in `lib/api.ts` and `parseSubmitError` in
 * `rebalances/RebalancesApprovedTable.tsx`.
 *
 * APP-1526: `missingKeys` moved from the top level of the body into `details`,
 * where every route-specific fact in the api's one error body lives
 * (`api/src/lib/errorBody.ts`). It is still the discriminator — the sibling
 * 409s on the same route carry `slantTokenRenewalRequired` and
 * `slantConnectionRequired` instead — so this parser still declines them.
 */
export function missingSlantDefinitions(err: unknown): string[] | null {
  const keys = serverError(err)?.body?.details?.missingKeys;
  if (!Array.isArray(keys) || keys.length === 0) {
    return null;
  }
  return keys.filter((key): key is string => typeof key === "string");
}

/**
 * The message a failed push carries when Slant refused the advisor's token, or
 * `null` when the failure is anything else.
 *
 * A 401 from Slant clears the stored credential, so the advisor must renew it
 * before sending again. The api answers that case with a 409 carrying
 * `slantTokenRenewalRequired` and a message naming the renewal
 * (`api/src/slant/billing-write-back.controller.ts`,
 * `s-slant-billing-write-back-019faf958af130ba` R23, AC16). Recovered from the
 * thrown `Error` the same way {@link missingSlantDefinitions} recovers
 * `missingKeys`, and for the same reason.
 *
 * APP-1526: the flag moved into `details` with the other discriminators
 * (`api/src/lib/errorBody.ts`); the renewal sentence is `message`, where it
 * already was. The test is still `=== true` rather than truthiness — the point
 * of a discriminator is that only the route that means it sets it.
 */
export function slantTokenRenewal(err: unknown): string | null {
  const body = serverError(err)?.body;
  if (body?.details?.slantTokenRenewalRequired !== true) {
    return null;
  }
  return typeof body.message === "string" ? body.message : null;
}

function useBulkAssignCashSettings() {
  return useAuthenticatedMutationAsync<{
    success: boolean;
    updatedCount: number;
  }>(
    `/v1/cash-settings/accounts`,
    async (data: { accountIds: number[]; cashSettingId: number | null }) => ({
      method: "PUT",
      body: JSON.stringify(data),
    }),
  );
}

const HouseholdCashBilling = ({
  household: propHousehold,
  isEditable = true,
}: {
  household?: HouseholdWithAccounts;
  isEditable?: boolean;
}) => {
  const { household: contextHousehold, schema } = useContext(HouseholdContext);
  const household = propHousehold ?? contextHousehold;
  const householdId = household?.id ?? 0;
  // The household GET carries the household's groups on today's date
  // (s-household-groups-group-level-019f18f8). `HouseholdWithAccounts` does not
  // name the key, so it is read with a default.
  const householdWithGroups = household as
    | (HouseholdWithAccounts & {
        householdGroups?: HouseholdGroupForHousehold[];
      })
    | undefined;
  const householdGroups = householdWithGroups?.householdGroups ?? [];
  // A household in two groups has no one group; its estimates show the
  // refusal instead (below).
  const householdGroup =
    householdGroups.length === 1 ? householdGroups[0] : null;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useFormContext<EditableHousehold>();

  const {
    isPending: isPendingFeeStructures,
    isError: isErrorFeeStructures,
    data: dataFeeStructures,
  } = useQueryFeeStructures();
  const feeStructures = dataFeeStructures?.data;

  const feeStructureOptions = [
    <option key={null} value="">
      None
    </option>,
    ...(feeStructures ?? [])
      .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
      .map((feeStructure) => (
        <option
          key={feeStructure.feeStructureId}
          value={feeStructure.feeStructureId}
        >
          {feeStructure.name}
        </option>
      )),
  ];

  const {
    isPending: isPendingBillingMinimums,
    isError: isErrorBillingMinimums,
    data: dataBillingMinimum,
  } = useQueryBillingMinimums();

  const billingMinimumOptions = useMemo(
    () => [
      <option key={null} value="">
        None
      </option>,
      ...(dataBillingMinimum?.data ?? [])
        .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
        .map((billingMinimum) => (
          <option key={billingMinimum.id} value={billingMinimum.id}>
            {billingMinimum.name}
          </option>
        )),
    ],
    [dataBillingMinimum?.data],
  );

  const updateHousehold = useAuthenticatedMutationAsync<
    UnpackResponse<HouseholdsController["updateHousehold"]>
  >(`/v1/households/${householdId}`, async (household: EditableHousehold) => ({
    method: "PUT",
    body: JSON.stringify(household),
  }));

  const notificationContext = useContext(NotificationContext);

  const { mutateAsync: onSubmit } = useMutation({
    mutationFn: async (data: EditableHousehold) => {
      try {
        const householdBody = await updateHousehold(data);
        reset(householdBody.data);
        notificationContext.pushNotification({
          id: `household-${householdId}`,
          header: "Household Updated",
          body: `${household?.name} updated`,
          variant: "success",
        });
      } catch (err) {
        console.error("Failed to save household", err);
        // HG-R6b: the api refuses a change of the fee structure or the billing
        // minimum of a grouped household with a 409 that names the group.
        const conflictMessage =
          err instanceof ApiError && err.status === 409
            ? serverError(err)?.body?.message
            : undefined;
        notificationContext.pushNotification({
          id: `household-${householdId}`,
          header: "Failed to Save Household",
          body:
            typeof conflictMessage === "string"
              ? conflictMessage
              : `${household?.name} was not saved`,
          variant: "danger",
        });
      }
    },
  });

  const {
    isPending: isPendingFeeEstimates,
    data: dataFeeEstimates,
    error: errorFeeEstimates,
  } = useAuthenticatedFetch<
    UnpackResponse<BillingController["getHouseholdFeeEstimates"]>
  >(`/v1/billing/fee-estimates/${householdId}`);
  const feeEstimates = dataFeeEstimates?.data;
  // The estimates of a household in two household groups are refused with the
  // sentence of the shared code path, which the tab shows in their place.
  const feeEstimatesErrorBody = serverError(errorFeeEstimates)?.body;
  const householdsInTwoGroupsFeeEstimates =
    feeEstimatesErrorBody?.details?.householdsInTwoGroups;
  const householdInTwoGroupsEstimateMessage =
    Array.isArray(householdsInTwoGroupsFeeEstimates) &&
    householdsInTwoGroupsFeeEstimates.length > 0 &&
    typeof feeEstimatesErrorBody?.message === "string"
      ? feeEstimatesErrorBody.message
      : null;

  // Every page of `GET /v1/cash-settings/accounts` (APP-1651).
  const {
    isPending: isPendingCashSettings,
    data: dataCashSettings,
    reload: reloadAccountCashSettings,
  } = useQueryAccountCashSettings();
  const cashSettings = dataCashSettings?.data;

  // Every page of `GET /v1/cash-settings` (APP-1651).
  const { isPending: isPendingAllCashSettings, data: dataAllCashSettings } =
    useQueryCashSettings();

  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [selectedCashSettingId, setSelectedCashSettingId] = useState<
    number | "remove" | null
  >(null);

  const { setError, resetError, errorMessage } = useErrorMessage();
  const bulkAssignMutation = useBulkAssignCashSettings();
  const [isAssigning, setIsAssigning] = useState(false);
  const queryClient = useQueryClient();

  // ── Send billing figures to Slant (s-slant-billing-write-back-019faf958af130ba) ──
  //
  // The trigger the advisor presses. It carries no credential: the api resolves
  // the Slant token from the pressing user (an-decision-019faaa50759d157), so
  // the control is shown only to a user whose own Slant connection exists.
  //
  // That connection is not enough on its own. The push writes to the
  // household's own Slant record, so a household with no Slant mapping — a
  // Wealthbox-linked one, or one on no CRM at all — has nothing to write to
  // and the api can only ever answer `pushed: false`. The control is gated on
  // the mapping too, so it is not offered where it could never do anything
  // (APP-1501).
  //
  // Pressing it twice is safe by design — it writes no note and provisions
  // nothing — but a second press while one is in flight would issue a second
  // Slant write for no gain, so the button is disabled for the duration.
  const { data: profileBody } =
    useAuthenticatedFetch<UnpackResponse<ProfileController["getProfile"]>>(
      "/profile",
    );
  const slantConnected = profileBody?.data.connections.slant === true;
  const linkedToSlant = household?.idExternal.slant != null;

  const pushBillingFieldsToSlant = useAuthenticatedMutationAsync<
    UnpackResponse<SlantBillingWriteBackController["pushHousehold"]>
  >(`/slant/billing-fields/households/${householdId}/push`, async () => ({
    method: "POST",
  }));

  const [isSendingToSlant, setIsSendingToSlant] = useState(false);
  // The api answers `pushed: false` when it issued no Slant call at all — the
  // household has no stored figures, or no mapping it has published to, or the
  // firm has configured no field. That is not a failure, so it is held apart
  // from the error message. The mapping reason outlives the render gate above:
  // a mapping can be removed between page load and press, and the api is the
  // one that checks at push time.
  const [nothingSentToSlant, setNothingSentToSlant] = useState(false);
  const {
    setError: setSlantError,
    resetError: resetSlantError,
    errorMessage: slantErrorMessage,
  } = useErrorMessage();

  const handleSendToSlant = useCallback(async () => {
    if (isSendingToSlant) {
      return;
    }
    setIsSendingToSlant(true);
    setNothingSentToSlant(false);
    resetSlantError();

    try {
      const body = await pushBillingFieldsToSlant();

      if (!body.data.pushed) {
        setNothingSentToSlant(true);
        return;
      }

      notificationContext.pushNotification({
        id: `household-${householdId}-slant-billing`,
        header: "Billing Figures Sent",
        body: `${household?.name} billing figures sent to Slant`,
        variant: "success",
      });
    } catch (err) {
      console.error("Failed to send billing figures to Slant", err);
      const missingKeys = missingSlantDefinitions(err);
      const tokenRenewal = slantTokenRenewal(err);
      setSlantError(
        missingKeys !== null
          ? `Slant has no definition for ${missingKeys.join(", ")}. An administrator provisions ${
              missingKeys.length === 1 ? "it" : "them"
            } on ${SLANT_BILLING_FIELDS_PAGE}, then this can be sent again.`
          : // R23/AC16: the api names the renewal, so show what it said rather
            // than restating it here and letting the two drift.
            (tokenRenewal ??
              "The write to Slant failed. Nothing in AdviceCloud changed."),
      );
    } finally {
      setIsSendingToSlant(false);
    }
  }, [
    isSendingToSlant,
    resetSlantError,
    setSlantError,
    pushBillingFieldsToSlant,
    notificationContext,
    householdId,
    household?.name,
  ]);

  const combinedCashSettingsAccountsHouseholds = useMemo(() => {
    const cashSettingsMap: Record<number, AccountCashSetting> = _.keyBy(
      cashSettings ?? [],
      "accountId",
    );
    const accountsMap: Record<number, AccountWORiskAllocation> = _.keyBy(
      household?.accounts ?? [],
      "id",
    );
    const feeStructuresMap = _.keyBy(feeStructures ?? [], "feeStructureId");
    const allCashSettingsMap: Record<number, CashSetting> = _.keyBy(
      dataAllCashSettings?.data ?? [],
      "id",
    );

    return Object.entries(accountsMap).map(([accountId, account]) => {
      const accountCashSetting = cashSettingsMap[parseInt(accountId)];
      const cashSettingId =
        accountCashSetting?.cashSettingId ?? account.cashSettingId;
      const fullCashSetting = cashSettingId
        ? allCashSettingsMap[cashSettingId]
        : undefined;

      const feeStructureId =
        account.feeStructureId ?? household?.feeStructureId ?? null;

      // Catch cases where a relocated account is no longer active
      const feeLocationAccountId =
        account.feeLocationAccountId == null ||
        typeof accountsMap[account.feeLocationAccountId] === "undefined"
          ? null
          : account.feeLocationAccountId;

      const result: HouseholdCashBillingRow = {
        ...account,
        ...(typeof accountCashSetting === "undefined"
          ? {}
          : accountCashSetting),
        cashSettingId: cashSettingId,
        cashSettingName: fullCashSetting?.name,
        feeStructureId: feeStructureId,
        feeStructureName:
          feeStructureId == null
            ? undefined
            : feeStructuresMap[feeStructureId]?.name,
        isFeeStructureInherited:
          feeStructureId != null && account.feeStructureId == null,
        feeLocation: account.feeLocation,
        feeLocationAccountId,
        feeLocationAccountName:
          account.feeLocation !== "A" || feeLocationAccountId == null
            ? undefined
            : displayAccountName(
                accountsMap[feeLocationAccountId].displayName,
                accountsMap[feeLocationAccountId].displayNumber,
              ),
      };

      return result;
    });
  }, [cashSettings, feeStructures, household, dataAllCashSettings?.data]);

  const { table, rowSelection, setRowSelection } = useHouseholdCashBillingTable(
    {
      accounts: combinedCashSettingsAccountsHouseholds,
      enableSelection: true,
    },
  );

  const selectedAccountIds = useMemo(() => {
    return Object.keys(rowSelection).map((id) => parseInt(id));
  }, [rowSelection]);

  const handleBulkAssignClick = useCallback(() => {
    if (selectedAccountIds.length === 0) {
      setError(
        "No Accounts selected, first select the accounts you would like to Bulk Assign",
      );
      return;
    }
    setSelectedCashSettingId(null);
    setShowBulkAssignModal(true);
  }, [selectedAccountIds.length, setError]);

  const handleModalClose = useCallback(() => {
    setShowBulkAssignModal(false);
    resetError();
  }, [resetError]);

  const handleBulkAssign = useCallback(async () => {
    if (!selectedCashSettingId) {
      setError("Please select a cash setting");
      return;
    }

    setIsAssigning(true);
    try {
      await bulkAssignMutation({
        accountIds: selectedAccountIds,
        cashSettingId:
          selectedCashSettingId === "remove" ? null : selectedCashSettingId,
      });

      setRowSelection({});
      setShowBulkAssignModal(false);

      // Walk the account cash settings again and refetch the household data.
      // The assign is not in the mutation cache, so
      // `useQueryAccountCashSettings` is reloaded here (APP-1651). The assign
      // changes no cash setting, so the list of cash settings
      // (`useQueryCashSettings`) is not read again.
      reloadAccountCashSettings();
      await queryClient.invalidateQueries({
        queryKey: [`/households/${householdId}`],
      });
      resetError();

      notificationContext.pushNotification({
        id: `household-${householdId}-bulk-assign`,
        header: "Cash Settings Updated",
        body: `Bulk assigned cash settings to ${selectedAccountIds.length} account${
          selectedAccountIds.length !== 1 ? "s" : ""
        }`,
        variant: "success",
      });
    } catch (err) {
      // The api's own sentence, when it sent one. Bulk assignment refuses the
      // whole batch when any account would be left with a pair R11 or R15
      // refuses, and its message names every such account
      // (an-decision-01a0ef5f76bd52b3, `bulkAssignCashSettingToAccounts`). A
      // fixed body would tell the advisor the assignment failed without telling
      // them which accounts to fix.
      setError(
        extractServerMessage(err) ??
          "Failed to assign cash settings to accounts",
      );
      console.error("Bulk assign error:", err);
    } finally {
      setIsAssigning(false);
    }
  }, [
    selectedCashSettingId,
    selectedAccountIds,
    bulkAssignMutation,
    setRowSelection,
    setError,
    resetError,
    reloadAccountCashSettings,
    queryClient,
    householdId,
    notificationContext,
  ]);

  const cashSettingsOptions = useMemo(() => {
    return [
      <option key="" value="">
        Select a cash setting
      </option>,
      <option key="remove" value="remove">
        Remove Cash Setting
      </option>,
      ...(dataAllCashSettings?.data ?? [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((setting) => (
          <option key={setting.id} value={setting.id}>
            {setting.name}
          </option>
        )),
    ];
  }, [dataAllCashSettings?.data]);

  return typeof register === "undefined" || typeof errors === "undefined" ? (
    <Loading />
  ) : (
    <Form onSubmit={handleSubmit((data) => onSubmit(data))}>
      <Row>
        <Col>
          <h3>Billing Settings</h3>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col xl={7}>
          {typeof household === "undefined" ||
          isPendingFeeStructures ||
          isPendingBillingMinimums ? (
            <Loading />
          ) : (
            <>
              {householdGroup === null ? null : (
                <Alert>
                  This household is billed as part of the household group{" "}
                  <Link
                    to={`/clients/households/household-groups/${householdGroup.householdGroupId}`}
                  >
                    {householdGroup.householdGroupName}
                  </Link>
                  . Its fee structure and billing minimum are set on the
                  household group.
                </Alert>
              )}
              <Row>
                <Col as="dt" xxl={4} md={6}>
                  {getSchemaFieldLabel(schema.fields.feeStructureId)}
                </Col>
                <Col as="dd" xxl={6} md={6}>
                  {isErrorFeeStructures ? (
                    <InlineError>
                      There was an error loading fee structures
                    </InlineError>
                  ) : householdGroup !== null ? (
                    // Not registered: the form keeps the household's own
                    // stored id, so a save does not change it (HG-R6a).
                    <Form.Select
                      disabled
                      value={householdGroup.feeStructureId ?? ""}
                    >
                      {feeStructureOptions}
                    </Form.Select>
                  ) : (
                    <Form.Select
                      placeholder="Select assigned fee structure"
                      {...register("feeStructureId")}
                    >
                      {feeStructureOptions}
                    </Form.Select>
                  )}
                  <FormFieldError field={errors.feeStructureId} />
                </Col>
              </Row>
              <Row>
                <Col as="dt" xxl={4} md={6}>
                  {getSchemaFieldLabel(schema.fields.billingMinimumId)}
                </Col>
                <Col as="dd" xxl={6} md={6}>
                  {isErrorBillingMinimums ? (
                    <InlineError>
                      There was an error loading billing minimums
                    </InlineError>
                  ) : householdGroup !== null ? (
                    <Form.Select
                      disabled
                      value={householdGroup.billingMinimumId ?? ""}
                    >
                      {billingMinimumOptions}
                    </Form.Select>
                  ) : (
                    <Form.Select
                      placeholder="Select assigned billing minimum"
                      {...register("billingMinimumId")}
                    >
                      {billingMinimumOptions}
                    </Form.Select>
                  )}
                  <FormFieldError field={errors.billingMinimumId} />
                </Col>
              </Row>
            </>
          )}
          <Row>
            <Col as="dt" xxl={4} md={6}>
              {getSchemaFieldLabel(schema.fields.feeChargeLocation)}
            </Col>
            <Col as="dd" xxl={6} md={6}>
              <Form.Select
                {...register("feeChargeLocation")}
                placeholder="Select fee location"
              >
                {feeChargeLocations.map((feeLocation, idx) => (
                  <option key={idx} value={feeLocation}>
                    {feeLocation}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Col>
        <Col xl={5}>
          {isPendingFeeEstimates ? (
            <Loading message="Fee Estimates" />
          ) : householdInTwoGroupsEstimateMessage !== null ? (
            <Alert variant="danger">
              {householdInTwoGroupsEstimateMessage}
            </Alert>
          ) : (
            <>
              <Row>
                <Col as="dt" xxl={4} md={6}>
                  Est. Effective Fee Rate
                </Col>
                <Col as="dd" xxl={8} md={6}>
                  {/* On 0 to 100 on the wire (`householdFeeEstimatesPercents`,
                      `api/src/billing/billing.controller.ts`). */}
                  {formatPercent(
                    (feeEstimates?.estEffectiveFeeRate ?? 0) / 100,
                    2,
                  )}
                </Col>
              </Row>
              <Row>
                <Col as="dt" xxl={4} md={6}>
                  Est. Annual Fee
                </Col>
                <Col as="dd" xxl={8} md={6}>
                  {formatCurrency(feeEstimates?.totalAnnualBill ?? 0)}
                </Col>
              </Row>
            </>
          )}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <h3>Account Cash Settings</h3>
          <ButtonToolbar className="mb-3">
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="Bulk Assign Cash Settings"
                icon="/icons/assign.svg"
                onClick={handleBulkAssignClick}
                disabled={selectedAccountIds.length === 0}
              />
            </ButtonGroup>
            {!slantConnected || !linkedToSlant ? null : (
              <ButtonGroup>
                {/* `type="button"`: this button sits inside the household form,
                    and a submit here would save the household as a side effect
                    of sending to Slant. */}
                <SubmitButton
                  type="button"
                  variant="secondary"
                  label="Send billing figures to Slant"
                  isSubmitting={isSendingToSlant}
                  onClick={handleSendToSlant}
                />
              </ButtonGroup>
            )}
          </ButtonToolbar>
          {slantErrorMessage}
          {!nothingSentToSlant ? null : (
            <Alert variant="warning">
              Nothing was sent to Slant and no Slant record changed. This
              household may have no figures stored yet — figures are stored when
              a billing report is finalized — or it may not be mapped to a Slant
              record, or the firm may have configured no billing fields to send.
              Check the household&apos;s mapping on{" "}
              <Link to="/clients/households/mapping">Data Connections</Link>; an
              administrator configures the fields on {SLANT_BILLING_FIELDS_PAGE}
              .
            </Alert>
          )}
          {isPendingCashSettings || isPendingAllCashSettings ? (
            <Loading message="Loading cash settings..." />
          ) : (
            <Table table={table} />
          )}
        </Col>
      </Row>
      <Row>
        <Col>{isEditable && <SubmitButton isSubmitting={isSubmitting} />}</Col>
      </Row>

      <Modal
        show={showBulkAssignModal}
        onHide={handleModalClose}
        aria-labelledby="bulk-assign-modal-title"
        scrollable={true}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="bulk-assign-modal-title">
            Assign Cash Setting
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorMessage}
          <Row className="mb-3">
            <Col>
              {selectedAccountIds.length} account
              {selectedAccountIds.length !== 1 ? "s" : ""} selected
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group controlId="household-cash-billing-bulk-assign-setting">
                <Form.Label>Cash Setting</Form.Label>
                <Form.Select
                  value={selectedCashSettingId ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedCashSettingId(
                      value === ""
                        ? null
                        : value === "remove"
                          ? "remove"
                          : Number(value),
                    );
                    resetError();
                  }}
                >
                  {cashSettingsOptions}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <ActionButton
            variant="secondary"
            label="Cancel"
            onClick={handleModalClose}
            disabled={isAssigning}
          />
          <SubmitButton
            label="Assign"
            onClick={handleBulkAssign}
            isSubmitting={isAssigning}
            disabled={!selectedCashSettingId}
          />
        </Modal.Footer>
      </Modal>
    </Form>
  );
};

export default HouseholdCashBilling;
